package gr.tech.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.blog.BLOG_ENTRIES
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.models.BlogEntry
import gr.tech.site.styles.BlogPreviewCardStyle
import gr.tech.site.styles.Theme
import gr.tech.site.styles.wideCappedWidth
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initBlogsPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Blog | Green Rebates News & Updates",
        description = "Trade show recaps, company wins, and behind-the-scenes updates from the Green Rebates team.",
        canonicalUrl = "https://greenrebates.com/blog"
    ))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun BlogsPage() {
    Column(
        modifier = Modifier
            .wideCappedWidth()
            .classNames("mt-5", "mx-auto", "mb-5"),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        H1(
            Modifier.classNames("fs-1", "fw-bold", "w-full", "text-center", "mb-2")
                .toAttrs()
        ) {
            Text("Blog")
        }

        P(
            Modifier.classNames("fs-5", "text-center", "mb-4", "w-75")
                .color(Theme.DarkGreen.rgb)
                .opacity(0.8)
                .toAttrs()
        ) {
            Text("Trade-show recaps and the latest industry news from the Green Rebates team.")
        }

        HorizontalDivider(modifier = Modifier.classNames("my-4"))

        Ul(
            attrs = Modifier
                .fillMaxWidth()
                .classNames("row", "row-cols-1", "row-cols-md-2", "row-cols-lg-3", "g-4", "list-unstyled")
                .toAttrs()
        ) {
            BLOG_ENTRIES.forEach { entry ->
                Li(attrs = Modifier.classNames("col").toAttrs()) {
                    BlogPreviewCard(entry)
                }
            }
        }
    }
}

@Composable
fun BlogPreviewCard(
    entry: BlogEntry,
    showShadow: Boolean = true
) {

    // Strip markdown bracket syntax from image path e.g. "[/images/foo.png]" → "/images/foo.png"
    val imageSrc = entry.image
        .removePrefix("[").removeSuffix("]").trim()
        .ifEmpty { ImageRepo.MoneyBackground.src }

    Link(
        path = SitePage.Blog.entryRoute(entry.routeOverride),
        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
        modifier = Modifier
            .fillMaxWidth()
            .textDecorationLine(TextDecorationLine.None)
    ) {
        Div(
            attrs = BlogPreviewCardStyle.toModifier()
                .classNames("card", "h-100")
                .then( other =
                    if (showShadow)
                        Modifier.classNames("shadow")
                    else
                        Modifier
                )
                .cursor(Cursor.Pointer)
                .toAttrs()
        ) {
            // Cover image
            Img(
                src = imageSrc,
                alt = entry.title,
                attrs = Modifier
                    .fillMaxWidth()
                    .height(180.px)
                    .objectFit(ObjectFit.Cover)
                    .display(DisplayStyle.Block)
                    .toAttrs { attr("loading", "lazy") }
            )

            // Card body
            Div(attrs = Modifier.classNames("card-body", "d-flex", "flex-column").toAttrs()) {
                H2(
                    Modifier
                        .classNames("fs-5", "fw-bold", "mb-1")
                        .color(Theme.DarkGreen.rgb)
                        .toAttrs()
                ) {
                    Text(entry.title)
                }

                P(
                    Modifier
                        .classNames("fs-6", "lh-base", "mb-0", "flex-grow-1")
                        .color(Theme.DarkGreen.rgb)
                        .opacity(0.75)
                        .toAttrs()
                ) {
                    Text(entry.desc)
                }
            }

            // Card footer — author + date
            Div(
                attrs = Modifier
                    .classNames("card-footer", "d-flex", "justify-content-between", "align-items-center")
                    .toAttrs()
            ) {
                Span(
                    Modifier
                        .classNames("fs-6", "fw-medium", "text-truncate", "me-2")
                        .color(Theme.MediumGreen.rgb)
                        .toAttrs()
                ) {
                    Text(entry.author)
                }

                Span(
                    Modifier
                        .classNames("fs-6", "text-nowrap", "d-none", "d-sm-inline")
                        .color(Theme.DarkGreen.rgb)
                        .opacity(0.6)
                        .toAttrs()
                ) {
                    Text(entry.date)
                }
            }
        }
    }
}
